export interface AudioNoiseGateParameters {
  enabled?: boolean;
  thresholdDb?: number;
  attackMs?: number;
  releaseMs?: number;
}

export interface AudioHighPassParameters {
  enabled?: boolean;
  cutoffHz?: number;
}

export interface AudioVoiceEnhancerParameters {
  enabled?: boolean;
  amount?: number;
}

export interface AudioCleanupParameters {
  enabled?: boolean;
  noiseGate?: AudioNoiseGateParameters | null;
  highPass?: AudioHighPassParameters | null;
  voiceEnhancer?: AudioVoiceEnhancerParameters | null;
}

export interface NormalizedAudioCleanupParameters {
  noiseGate: {
    thresholdDb: number;
    attackMs: number;
    releaseMs: number;
  } | null;
  highPass: {
    cutoffHz: number;
  } | null;
  voiceEnhancer: {
    amount: number;
  } | null;
}

export interface AudioCleanupProcessor {
  processFrame(frame: Float32Array): void;
}

export const AUDIO_CLEANUP_DEFAULTS = {
  noiseGate: {
    thresholdDb: -48,
    attackMs: 5,
    releaseMs: 120,
  },
  highPass: {
    cutoffHz: 80,
  },
  voiceEnhancer: {
    amount: 0.5,
  },
} as const;

export const AUDIO_CLEANUP_LIMITS = {
  noiseGate: {
    thresholdDb: { min: -96, max: 0 },
    attackMs: { min: 0, max: 250 },
    releaseMs: { min: 0, max: 2000 },
  },
  highPass: {
    cutoffHz: { min: 20, max: 2000 },
  },
  voiceEnhancer: {
    amount: { min: 0, max: 1 },
  },
} as const;

interface BiquadCoefficients {
  b0: number;
  b1: number;
  b2: number;
  a1: number;
  a2: number;
}

interface BiquadState {
  z1: number;
  z2: number;
}

export function normalizeAudioCleanupParameters(
  parameters: AudioCleanupParameters | null | undefined,
): NormalizedAudioCleanupParameters | null {
  if (!parameters || parameters.enabled === false) return null;

  const noiseGate = blockEnabled(parameters.noiseGate)
    ? {
        thresholdDb: clampedFinite(
          parameters.noiseGate?.thresholdDb,
          AUDIO_CLEANUP_DEFAULTS.noiseGate.thresholdDb,
          AUDIO_CLEANUP_LIMITS.noiseGate.thresholdDb.min,
          AUDIO_CLEANUP_LIMITS.noiseGate.thresholdDb.max,
        ),
        attackMs: clampedFinite(
          parameters.noiseGate?.attackMs,
          AUDIO_CLEANUP_DEFAULTS.noiseGate.attackMs,
          AUDIO_CLEANUP_LIMITS.noiseGate.attackMs.min,
          AUDIO_CLEANUP_LIMITS.noiseGate.attackMs.max,
        ),
        releaseMs: clampedFinite(
          parameters.noiseGate?.releaseMs,
          AUDIO_CLEANUP_DEFAULTS.noiseGate.releaseMs,
          AUDIO_CLEANUP_LIMITS.noiseGate.releaseMs.min,
          AUDIO_CLEANUP_LIMITS.noiseGate.releaseMs.max,
        ),
      }
    : null;

  const rawCutoffHz = finiteOr(
    parameters.highPass?.cutoffHz,
    AUDIO_CLEANUP_DEFAULTS.highPass.cutoffHz,
  );
  const highPass = blockEnabled(parameters.highPass) && rawCutoffHz > 0
    ? {
        cutoffHz: clamp(
          rawCutoffHz,
          AUDIO_CLEANUP_LIMITS.highPass.cutoffHz.min,
          AUDIO_CLEANUP_LIMITS.highPass.cutoffHz.max,
        ),
      }
    : null;

  const rawVoiceAmount = finiteOr(
    parameters.voiceEnhancer?.amount,
    AUDIO_CLEANUP_DEFAULTS.voiceEnhancer.amount,
  );
  const voiceAmount = clamp(
    rawVoiceAmount,
    AUDIO_CLEANUP_LIMITS.voiceEnhancer.amount.min,
    AUDIO_CLEANUP_LIMITS.voiceEnhancer.amount.max,
  );
  const voiceEnhancer = blockEnabled(parameters.voiceEnhancer) && voiceAmount > 0
    ? { amount: voiceAmount }
    : null;

  if (!noiseGate && !highPass && !voiceEnhancer) return null;
  return { noiseGate, highPass, voiceEnhancer };
}

export function isAudioCleanupEnabled(
  parameters: AudioCleanupParameters | null | undefined,
): boolean {
  return normalizeAudioCleanupParameters(parameters) !== null;
}

export function createAudioCleanupProcessor(
  sampleRate: number,
  channelCount: number,
  parameters: AudioCleanupParameters | null | undefined,
): AudioCleanupProcessor | null {
  const normalized = normalizeAudioCleanupParameters(parameters);
  if (!normalized || !validSampleRate(sampleRate) || !Number.isFinite(channelCount) || channelCount < 1) {
    return null;
  }
  return createNormalizedAudioCleanupProcessor(sampleRate, Math.floor(channelCount), normalized);
}

export function applyAudioCleanup(
  channelData: Float32Array[],
  sampleRate: number,
  parameters: AudioCleanupParameters | null | undefined,
): Float32Array[] {
  const processor = createAudioCleanupProcessor(sampleRate, channelData.length, parameters);
  if (!processor || !channelData.some(channel => channel.length > 0)) return channelData;
  const output = channelData.map(channel => channel.slice());
  processPlanarAudioInPlace(output, processor);
  return output;
}

export function applyAudioCleanupInPlace(
  channelData: Float32Array[],
  sampleRate: number,
  parameters: AudioCleanupParameters | null | undefined,
): boolean {
  const processor = createAudioCleanupProcessor(sampleRate, channelData.length, parameters);
  if (!processor || !channelData.some(channel => channel.length > 0)) return false;
  processPlanarAudioInPlace(channelData, processor);
  return true;
}

function createNormalizedAudioCleanupProcessor(
  sampleRate: number,
  channelCount: number,
  parameters: NormalizedAudioCleanupParameters,
): AudioCleanupProcessor {
  const highPass = parameters.highPass
    ? highPassCoefficients(Math.min(parameters.highPass.cutoffHz, sampleRate * 0.45), sampleRate)
    : null;
  const highPassStates = highPass ? createBiquadStates(channelCount) : null;
  const voiceEnhancer = parameters.voiceEnhancer
    ? peakingEqCoefficients(
        Math.min(3000, sampleRate * 0.35),
        sampleRate,
        0.9,
        parameters.voiceEnhancer.amount * 6,
      )
    : null;
  const voiceEnhancerStates = voiceEnhancer ? createBiquadStates(channelCount) : null;
  const gateThreshold = parameters.noiseGate
    ? Math.pow(10, parameters.noiseGate.thresholdDb / 20)
    : 0;
  const gateAttackRetention = parameters.noiseGate
    ? smoothingRetention(parameters.noiseGate.attackMs, sampleRate)
    : 0;
  const gateReleaseRetention = parameters.noiseGate
    ? smoothingRetention(parameters.noiseGate.releaseMs, sampleRate)
    : 0;
  let gateGain = 0;

  return {
    processFrame(frame: Float32Array): void {
      const channels = Math.min(channelCount, frame.length);

      if (highPass && highPassStates) {
        for (let channel = 0; channel < channels; channel++) {
          frame[channel] = processBiquadSample(frame[channel], highPass, highPassStates[channel]);
        }
      }

      if (parameters.noiseGate) {
        let detector = 0;
        for (let channel = 0; channel < channels; channel++) {
          detector = Math.max(detector, Math.abs(frame[channel]));
        }
        const target = detector >= gateThreshold ? 1 : 0;
        const retention = target > gateGain ? gateAttackRetention : gateReleaseRetention;
        gateGain = target + retention * (gateGain - target);
        if (gateGain < 1e-8) gateGain = 0;
        for (let channel = 0; channel < channels; channel++) frame[channel] *= gateGain;
      }

      if (voiceEnhancer && voiceEnhancerStates) {
        for (let channel = 0; channel < channels; channel++) {
          frame[channel] = processBiquadSample(frame[channel], voiceEnhancer, voiceEnhancerStates[channel]);
        }
      }
    },
  };
}

function processPlanarAudioInPlace(channelData: Float32Array[], processor: AudioCleanupProcessor): void {
  let frameCount = 0;
  for (const channel of channelData) frameCount = Math.max(frameCount, channel.length);
  const frame = new Float32Array(channelData.length);
  for (let frameIndex = 0; frameIndex < frameCount; frameIndex++) {
    for (let channel = 0; channel < channelData.length; channel++) {
      frame[channel] = frameIndex < channelData[channel].length ? channelData[channel][frameIndex] : 0;
    }
    processor.processFrame(frame);
    for (let channel = 0; channel < channelData.length; channel++) {
      if (frameIndex < channelData[channel].length) channelData[channel][frameIndex] = frame[channel];
    }
  }
}

function highPassCoefficients(cutoffHz: number, sampleRate: number): BiquadCoefficients {
  const omega = (2 * Math.PI * cutoffHz) / sampleRate;
  const cosine = Math.cos(omega);
  const alpha = Math.sin(omega) / (2 * Math.SQRT1_2);
  const a0 = 1 + alpha;
  return {
    b0: ((1 + cosine) / 2) / a0,
    b1: (-(1 + cosine)) / a0,
    b2: ((1 + cosine) / 2) / a0,
    a1: (-2 * cosine) / a0,
    a2: (1 - alpha) / a0,
  };
}

function peakingEqCoefficients(
  centerHz: number,
  sampleRate: number,
  q: number,
  gainDb: number,
): BiquadCoefficients {
  const amplitude = Math.pow(10, gainDb / 40);
  const omega = (2 * Math.PI * centerHz) / sampleRate;
  const cosine = Math.cos(omega);
  const alpha = Math.sin(omega) / (2 * q);
  const a0 = 1 + alpha / amplitude;
  return {
    b0: (1 + alpha * amplitude) / a0,
    b1: (-2 * cosine) / a0,
    b2: (1 - alpha * amplitude) / a0,
    a1: (-2 * cosine) / a0,
    a2: (1 - alpha / amplitude) / a0,
  };
}

function createBiquadStates(channelCount: number): BiquadState[] {
  return Array.from({ length: channelCount }, () => ({ z1: 0, z2: 0 }));
}

function processBiquadSample(
  input: number,
  coefficients: BiquadCoefficients,
  state: BiquadState,
): number {
  const output = coefficients.b0 * input + state.z1;
  state.z1 = coefficients.b1 * input - coefficients.a1 * output + state.z2;
  state.z2 = coefficients.b2 * input - coefficients.a2 * output;
  return output;
}

function smoothingRetention(timeMs: number, sampleRate: number): number {
  if (!(timeMs > 0)) return 0;
  return Math.exp(-1 / (timeMs * 0.001 * sampleRate));
}

function blockEnabled(block: { enabled?: boolean } | null | undefined): boolean {
  return !!block && block.enabled !== false;
}

function validSampleRate(sampleRate: number): boolean {
  return Number.isFinite(sampleRate) && sampleRate > 0;
}

function clampedFinite(value: number | undefined, fallback: number, min: number, max: number): number {
  return clamp(finiteOr(value, fallback), min, max);
}

function finiteOr(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
